import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { sqlRead, clean } from "../../utils/sqlClient";
import { fmtDate } from "../../utils/format";

function daysUntil(d) {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date()) / 86400000);
}

const DEFAULT_MILESTONES = [
  "RFI Cutoff",
  "Quote Deadline",
  "Internal Review",
  "Bid Day",
];

// The common milestones not yet on this estimate, in the usual order (a name already present is matched ignoring case).
export const remainingDefaultMilestones = (milestones = []) => {
  const have = new Set(milestones.map(m => String(m.MilestoneName || "").trim().toLowerCase()));
  return DEFAULT_MILESTONES.filter(n => !have.has(n.toLowerCase()));
};

export default function Milestones({ projectId }) {
  const { accounts } = useMsal();
  const user = accounts[0];

  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAdd, setShowAdd]       = useState(false);
  const [editingId, setEditingId]   = useState(null);

  useEffect(() => {
    sqlRead(`EXEC sp_GetMilestones @project_id=${projectId}`)
      .then(rows => setMilestones(rows))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId]);

  const toggleComplete = async (m) => {
    const updated = { ...m, IsComplete: !m.IsComplete };
    setMilestones(prev => prev.map(x => x.MilestoneID === m.MilestoneID ? updated : x));
    await sqlRead(`EXEC sp_SaveMilestone
      @milestone_id=${m.MilestoneID},
      @project_id=${projectId},
      @milestone_name='${clean(m.MilestoneName)}',
      @due_date=${m.DueDate ? `'${m.DueDate.slice(0,10)}'` : "NULL"},
      @is_complete=${updated.IsComplete ? 1 : 0},
      @saved_by='${clean(user?.name || "")}'`).catch(console.error);
  };

  const addQuickMilestone = async (name) => {
    const rows = await sqlRead(`EXEC sp_SaveMilestone
      @project_id=${projectId},
      @milestone_name='${clean(name)}',
      @display_order=${(milestones.length + 1) * 10},
      @saved_by='${clean(user?.name || "")}'`).catch(() => []);
    if (rows[0]?.MilestoneID) {
      setMilestones(prev => [...prev, { MilestoneID: rows[0].MilestoneID, MilestoneName: name, DueDate: null, IsComplete: false, DisplayOrder: (milestones.length + 1) * 10 }]);
    }
  };

  const upcoming  = milestones.filter(m => !m.IsComplete).sort((a, b) => (a.DueDate || "9999") < (b.DueDate || "9999") ? -1 : 1);
  const completed = milestones.filter(m => m.IsComplete);

  if (loading) return <div style={{ color: "var(--text3)", fontSize: 13 }}>Loading milestones…</div>;

  return (
    <div style={{ maxWidth: 620 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div className="panel-title">Estimate Milestones</div>
          <div className="panel-sub">Schedule anchors for this estimate. Assign checklist items to milestones to turn the BOD into a deadline-driven to-do list.</div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          style={{ padding: "8px 14px", fontSize: 12, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}
        >
          + Add milestone
        </button>
      </div>

      {/* Quick-add: stays until every common milestone has been added (it used to vanish after the first one — feedback #1) */}
      {remainingDefaultMilestones(milestones).length > 0 && (
        <div style={{ marginBottom: 20, padding: "16px", background: "var(--cs-blue-lt)", borderRadius: "var(--radius)", border: "1px solid rgba(24,95,165,0.15)" }}>
          <div style={{ fontSize: 12, color: "var(--cs-blue)", fontWeight: 600, marginBottom: 10 }}>{milestones.length === 0 ? "Quick-add common milestones:" : "Still to add:"}</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {remainingDefaultMilestones(milestones).map(name => (
              <button
                key={name}
                onClick={() => addQuickMilestone(name)}
                style={{ fontSize: 12, padding: "5px 12px", borderRadius: 100, background: "var(--surface)", border: "1px solid rgba(24,95,165,0.25)", color: "var(--cs-blue)", cursor: "pointer", fontWeight: 500 }}
              >
                + {name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming milestones */}
      {upcoming.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Upcoming — {upcoming.length}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {upcoming.map(m => <MilestoneRow key={m.MilestoneID} milestone={m} onToggle={() => toggleComplete(m)} onEdit={() => setEditingId(m.MilestoneID)} />)}
          </div>
        </div>
      )}

      {/* Completed */}
      {completed.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Completed — {completed.length}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, opacity: 0.65 }}>
            {completed.map(m => <MilestoneRow key={m.MilestoneID} milestone={m} onToggle={() => toggleComplete(m)} onEdit={() => setEditingId(m.MilestoneID)} />)}
          </div>
        </div>
      )}

      {milestones.length === 0 && !showAdd && (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--text3)", fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📅</div>
          No milestones yet. Add RFI cutoff, quote deadline, and bid day to get started.
        </div>
      )}

      {/* Add / Edit form */}
      {(showAdd || editingId) && (
        <MilestoneForm
          projectId={projectId}
          milestone={editingId ? milestones.find(m => m.MilestoneID === editingId) : null}
          savedBy={user?.name}
          displayOrder={(milestones.length + 1) * 10}
          onSaved={(m) => {
            if (editingId) {
              setMilestones(prev => prev.map(x => x.MilestoneID === m.MilestoneID ? m : x));
              setEditingId(null);
            } else {
              setMilestones(prev => [...prev, m]);
              setShowAdd(false);
            }
          }}
          onCancel={() => { setShowAdd(false); setEditingId(null); }}
        />
      )}
    </div>
  );
}

function MilestoneRow({ milestone: m, onToggle, onEdit }) {
  const days = daysUntil(m.DueDate);
  const overdue  = !m.IsComplete && days !== null && days < 0;
  const urgent   = !m.IsComplete && days !== null && days <= 3 && days >= 0;

  const barColor = m.IsComplete ? "var(--green)" : overdue ? "var(--red)" : urgent ? "#d97706" : "var(--cs-blue)";

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "10px 14px",
      background: "var(--surface)",
      border: "1px solid var(--cs-border)",
      borderLeft: `3px solid ${barColor}`,
      borderRadius: "var(--radius)",
    }}>
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={!!m.IsComplete}
        onChange={onToggle}
        style={{ width: 15, height: 15, cursor: "pointer", flexShrink: 0, accentColor: "var(--cs-navy)" }}
      />

      {/* Name + date */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: m.IsComplete ? "var(--text3)" : "var(--text)", textDecoration: m.IsComplete ? "line-through" : "none" }}>
          {m.MilestoneName}
        </div>
        {m.Notes && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>{m.Notes}</div>}
      </div>

      {/* Due date + countdown */}
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        {m.DueDate ? (
          <>
            <div style={{ fontSize: 12, fontWeight: 600, color: m.IsComplete ? "var(--text3)" : barColor }}>
              {m.IsComplete ? "Done" : days === 0 ? "Today" : days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
            </div>
            <div style={{ fontSize: 10, color: "var(--text3)" }}>{fmtDate(m.DueDate)}</div>
          </>
        ) : (
          <span style={{ fontSize: 11, color: "var(--text3)" }}>No date</span>
        )}
      </div>

      {/* Edit */}
      <button onClick={onEdit} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--text3)", padding: "2px 6px", flexShrink: 0 }}>
        ✎
      </button>
    </div>
  );
}

function MilestoneForm({ projectId, milestone, savedBy, displayOrder, onSaved, onCancel }) {
  const [name, setName]   = useState(milestone?.MilestoneName || "");
  const [date, setDate]   = useState(milestone?.DueDate ? milestone.DueDate.slice(0,10) : "");
  const [notes, setNotes] = useState(milestone?.Notes || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const mid = milestone?.MilestoneID || "NULL";
      const rows = await sqlRead(`EXEC sp_SaveMilestone
        @milestone_id=${mid === "NULL" ? "NULL" : mid},
        @project_id=${projectId},
        @milestone_name='${clean(name)}',
        @due_date=${date ? `'${date}'` : "NULL"},
        @notes='${clean(notes)}',
        @display_order=${milestone?.DisplayOrder || displayOrder},
        @saved_by='${clean(savedBy || "")}'`);
      onSaved({
        MilestoneID: rows[0]?.MilestoneID || milestone?.MilestoneID,
        MilestoneName: name, DueDate: date || null,
        Notes: notes, IsComplete: milestone?.IsComplete || false,
        DisplayOrder: milestone?.DisplayOrder || displayOrder,
      });
    } catch(e) { console.error(e); }
    setSaving(false);
  };

  const inputStyle = { height: 30, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 9px", fontSize: 12, color: "var(--text)", width: "100%", outline: "none", boxSizing: "border-box" };
  const labelStyle = { fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 3 };

  return (
    <div style={{ marginTop: 16, padding: 16, background: "var(--surface2)", borderRadius: "var(--radius)", border: "1px solid var(--cs-border)" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", marginBottom: 12 }}>
        {milestone ? "Edit milestone" : "New milestone"}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 10, marginBottom: 10 }}>
        <div>
          <label style={labelStyle}>Milestone name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. RFI Cutoff" autoFocus style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Due date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Notes</label>
        <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes" style={inputStyle} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={onCancel} style={{ padding: "6px 12px", fontSize: 12, background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)" }}>Cancel</button>
        <button onClick={save} disabled={saving || !name.trim()} style={{ padding: "6px 14px", fontSize: 12, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}>
          {saving ? "Saving…" : milestone ? "Save changes" : "Add milestone"}
        </button>
      </div>
    </div>
  );
}
